import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { alpha } from '@mui/material/styles';
import COLORS from '../../constants/colors';
import { APP_HEADER_HEIGHT } from '../../constants/layout';
import { SideTabs, sideTabId, sideTabPanelId } from './SideTabs';
import { SideTabDescriptor, SideTabSection } from './types';

interface SideTabsLayoutProps {
  sections: SideTabSection[];
  ariaLabel: string;
  value: string;
  onChange: (value: string) => void;
  isTabDisabled?: (tab: SideTabDescriptor) => boolean;
  /** The active panel's content. */
  children: React.ReactNode;
}

const never = () => false;

/**
 * Sticky vertical tab rail beside its panel. Detail pages with a long tab strip
 * use this instead of a horizontal scroller, where the tabs at either end are
 * invisible until you scroll to them.
 */
export function SideTabsLayout({
  sections,
  ariaLabel,
  value,
  onChange,
  isTabDisabled = never,
  children,
}: SideTabsLayoutProps) {
  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'flex-start', gap: '16px' }}>
      <Paper
        elevation={0}
        sx={{
          width: { xs: '100%', md: '232px' },
          flexShrink: 0,
          p: '12px',
          borderRadius: '12px',
          border: `1px solid ${alpha(COLORS.MAIN_DARK, 0.08)}`,
          position: { xs: 'static', md: 'sticky' },
          // Clear the fixed app header, otherwise the rail sticks underneath it.
          top: `${APP_HEADER_HEIGHT + 16}px`,
        }}
      >
        <SideTabs
          sections={sections}
          ariaLabel={ariaLabel}
          value={value}
          onChange={onChange}
          isTabDisabled={isTabDisabled}
        />
      </Paper>

      <Box
        role="tabpanel"
        id={sideTabPanelId(value)}
        aria-labelledby={sideTabId(value)}
        sx={{ flex: '1 1 auto', minWidth: 0, width: '100%' }}
      >
        {children}
      </Box>
    </Box>
  );
}
